/**
 * „Ersetzen durch …" — alle Rollen eines Assets auf ein anderes übertragen.
 *
 * Gewünscht im Termin mit der HOWOGE am 07.10.2026 (Punkt 16): wird eine
 * Anwendung durch eine andere abgelöst, trug bisher jemand jede Zuordnung von
 * Hand um. Jetzt geht es in einem Vorgang:
 *
 * 1. **Ziel wählen** — ein vorhandenes Asset gleicher Objektart oder ein
 *    neues, vorbelegt wie „Als Kopie anlegen". Ein neues entsteht mit der
 *    Vorschau, denn erst ein bestehendes Ziel lässt sich zählen; es trägt
 *    dieselbe Vorgangskennung wie das Ersetzen.
 * 2. **Vorschau** — ein Trockenlauf zählt, was übertragen wird und was am Ziel
 *    schon vorhanden ist. Er liefert zugleich einen Stand.
 * 3. **Ausführen** — mit diesem Stand. Hat sich inzwischen etwas geändert,
 *    weist die Schnittstelle ab (409), und die Vorschau ist neu zu rechnen.
 *
 * „Alte Applikation archivieren" ist vorbelegt an (Entscheidung vom
 * 07.10.2026). Archiviert heißt: aus den Listen genommen, als Nachweis
 * erhalten.
 */

import { useState } from 'react';
import { stil } from './stil';
import { useToast } from './Toast';
import { useEntprellt, useListe, bestandGeaendert } from '../api/haken';
import { ApiFehler } from '../api/klient';
import { assetErsetzen, type Ersetzergebnis } from '../api/sammelzuordnung';
import {
  ausFormularAnlegen, assetTypAusFormular, kategorieAusFormular, namensfeldAusFormular,
} from '../api/anlegen';
import { GRUND_MINDESTENS } from '../api/stilllegen';

interface AssetZeile { assetId: string; name: string; assetTyp: string; kategorie: string | null }

/** Die Quelle — das Asset, dessen Rollen übertragen werden. */
export interface Ersetzquelle {
  assetId: string;
  version: string;
  name: string;
  assetTyp: string;
  /** Teil der Identität: AVD- und Surface-Fassung tragen denselben Namen. */
  kategorie: string;
}

/** „Alte Applikation archivieren" — je Objektart mit dem passenden Artikel. */
const ARCHIV_LABEL: Record<string, string> = {
  Applikation: 'Alte Applikation archivieren',
  MobileApp: 'Alte Applikation archivieren',
  Fileserver: 'Altes Laufwerk archivieren',
  Postfach: 'Altes Postfach archivieren',
  Verteiler: 'Alten Verteiler archivieren',
  M365: 'Alte Gruppe archivieren',
  Hardware: 'Alte Hardware archivieren',
};

const zahl = (n: number) => new Intl.NumberFormat('de-DE').format(n);

export function Ersetzenweg({
  mandant, formular, werte, quelle, ungespeichert, onZurueck, onFertig,
}: {
  mandant: string;
  /** Das Formular, aus dem ein neues Ziel angelegt würde. */
  formular: string;
  /** Die Werte des Formulars — die Vorbelegung eines neuen Ziels. */
  werte: Record<string, string>;
  quelle: Ersetzquelle;
  /** Stehen im Formular ungespeicherte Eingaben? Sie gehen nicht mit. */
  ungespeichert: boolean;
  onZurueck: () => void;
  onFertig: () => void;
}) {
  const melden = useToast();
  const [art, setzeArt] = useState<'vorhanden' | 'neu'>('vorhanden');
  const [suche, setzeSuche] = useState('');
  const [ziel, setzeZiel] = useState<AssetZeile | null>(null);
  const [neuName, setzeNeuName] = useState(quelle.name + ' (Kopie)');
  const [archivieren, setzeArchivieren] = useState(true);
  const [grund, setzeGrund] = useState('');
  const [vorschau, setzeVorschau] = useState<Ersetzergebnis | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<string | null>(null);
  /* Eine Kennung für alles, was dieses Ersetzen schreibt — auch für ein neu
     angelegtes Ziel. Daran erkennt die Schnittstelle es, solange es noch
     zur Freigabe liegt. */
  const [vorgang] = useState(() => crypto.randomUUID());

  /* Jede Änderung am Ziel macht die Vorschau ungültig — sie zählte für ein
     anderes. Die Begründung gehört nicht dazu: sie ändert keine Zahl. */
  const zurueck = () => { setzeVorschau(null); setzeFehler(null); };
  const artWaehlen = (neu: 'vorhanden' | 'neu') => { setzeArt(neu); zurueck(); };
  const zielWaehlen = (a: AssetZeile) => { setzeZiel(a); zurueck(); };

  const gesucht = useEntprellt(suche.trim(), 250);
  const kandidaten = useListe<AssetZeile>('assets', mandant, {
    seite: 1, groesse: 20, sortierung: 'name', richtung: 'auf',
    filter: { assetTyp: quelle.assetTyp },
    ...(gesucht ? { suche: gesucht } : {}),
    aus: art !== 'vorhanden',
  });
  /* Ohne die Quelle selbst — erkannt an Name **und** Kategorie, denn der
     Zwilling gleichen Namens (AVD/Surface) ist ein gültiges Ziel. */
  const liste = (kandidaten.daten?.zeilen ?? []).filter((a) => a.assetId !== quelle.assetId
    && !(a.name === quelle.name && (a.kategorie ?? '') === quelle.kategorie));

  const neuerName = neuName.trim().replace(/\s+/g, ' ');
  /* Ein neues Ziel entsteht aus dem Formular. Ergäbe das eine andere
     Objektart als die Quelle — etwa eine mobile App, deren Kategorie keine
     iPhone- oder iPad-Kategorie ist —, wiese die Schnittstelle das Ersetzen
     ab. Gesagt wird es vorher. */
  const neuerTyp = assetTypAusFormular(formular, werte);
  const neuMoeglich = neuerTyp === quelle.assetTyp;
  const neueKategorie = kategorieAusFormular(formular, werte);
  /* Gleich ist ein Ziel nur bei gleichem Namen **und** gleicher Kategorie —
     eine Surface-Fassung unter demselben Namen ist ein anderes Asset. */
  const neuGleichQuelle = neuerName === quelle.name && neueKategorie === quelle.kategorie;
  const nach = art === 'vorhanden'
    ? (ziel ? { asset: ziel.name, assetTyp: ziel.assetTyp, kategorie: ziel.kategorie ?? '' } : null)
    : (neuMoeglich && neuerName && !neuGleichQuelle
      ? { asset: neuerName, assetTyp: quelle.assetTyp, kategorie: neueKategorie } : null);
  const grundOk = grund.trim().length >= GRUND_MINDESTENS;

  const anfrage = (trockenlauf: boolean, ziel2: { asset: string; assetTyp: string; kategorie: string }) => ({
    von: { asset: quelle.name, assetTyp: quelle.assetTyp, kategorie: quelle.kategorie },
    nach: ziel2,
    altesAsset: archivieren ? { assetId: quelle.assetId, version: quelle.version } : null,
    begruendung: grund.trim(),
    trockenlauf,
    vorgangId: vorgang,
    ...(!trockenlauf && vorschau ? { stand: vorschau.stand } : {}),
  });

  /**
   * Die Vorschau — bei einem neuen Ziel mit dem Anlegen davor.
   *
   * Zählen lässt sich nur gegen ein Ziel, das es gibt. Das neue Asset
   * entsteht deshalb hier und nicht erst beim Ausführen; der Knopf sagt das.
   * Danach gilt es als vorhandenes Ziel — ein zweiter Klick legte sonst ein
   * zweites an.
   */
  const vorschauRechnen = async () => {
    if (!nach || !grundOk || laeuft) return;
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      let ziel2 = nach;
      if (art === 'neu') {
        const namensfeld = namensfeldAusFormular(formular) ?? 'name';
        const ergebnis = await ausFormularAnlegen(
          mandant, formular, { ...werte, [namensfeld]: neuerName }, grund.trim(), vorgang);
        const angelegt: AssetZeile = {
          assetId: ergebnis.schluessel,
          name: String(ergebnis.satz?.['name'] ?? neuerName),
          assetTyp: quelle.assetTyp,
          kategorie: (ergebnis.satz?.['kategorie'] as string | null | undefined) ?? neueKategorie,
        };
        setzeArt('vorhanden');
        setzeZiel(angelegt);
        ziel2 = { asset: angelegt.name, assetTyp: angelegt.assetTyp, kategorie: angelegt.kategorie ?? '' };
        bestandGeaendert(ergebnis.freigabe ? 'freigaben' : 'assets');
        melden(ergebnis.freigabe ? 'Neues Asset zur Freigabe eingereicht' : 'Neues Asset angelegt',
          `„${angelegt.name}“ ist das Ziel des Ersetzens.`, ergebnis.freigabe ? 'hinweis' : 'erfolg');
      }
      setzeVorschau(await assetErsetzen(mandant, anfrage(true, ziel2)));
    } catch (f) {
      setzeFehler(f instanceof Error ? f.message : 'Unbekannter Fehler.');
    } finally {
      setzeLaeuft(false);
    }
  };

  const ausfuehren = async () => {
    if (!nach || !grundOk || !vorschau || laeuft) return;
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      const e = await assetErsetzen(mandant, anfrage(false, nach));
      const ziel2 = nach.asset;
      const teile = [
        `${zahl(e.uebernommen)} ${e.uebernommen === 1 ? 'Zuordnung' : 'Zuordnungen'} auf „${ziel2}“ übertragen`,
      ];
      if (e.uebersprungen > 0) teile.push(`${zahl(e.uebersprungen)} waren dort schon vorhanden`);
      if (e.assetArchiviert) teile.push(`„${quelle.name}“ ist archiviert`);
      melden(
        e.freigabe ? 'Zur Freigabe eingereicht' : 'Ersetzt',
        teile.join(' · ') + '.'
          + (e.freigabe
            ? ' Ein Teil liegt zur Freigabe — wird nur ein Teil davon bestätigt, sind '
              + 'alte und neue Anwendung eine Zeit lang beide zugeordnet.'
            : ''),
        e.freigabe ? 'hinweis' : 'erfolg',
      );
      bestandGeaendert('zuordnungen');
      bestandGeaendert('assets');
      bestandGeaendert('rollen');
      if (e.freigabe) bestandGeaendert('freigaben');
      onFertig();
    } catch (f) {
      if (f instanceof ApiFehler && f.status === 409) {
        setzeVorschau(null);
        setzeFehler('Der Bestand hat sich seit der Vorschau geändert — jemand hat an den '
          + 'Zuordnungen oder am Asset geschrieben. Bitte die Vorschau neu berechnen.');
      } else {
        setzeFehler(f instanceof Error ? f.message : 'Unbekannter Fehler.');
      }
    } finally {
      setzeLaeuft(false);
    }
  };

  const abschnitt = (titel: string) => (
    <div style={stil(
      'font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;'
      + 'color:var(--text-tertiary);margin-bottom:10px',
    )}>{titel}</div>
  );

  return (
    <>
      <div style={stil('flex:1;min-height:0;overflow-y:auto;padding:16px 20px 20px')}>
        <p style={stil('margin:0 0 16px;font-size:13px;line-height:20px;color:var(--text-secondary)')}>
          Alle Rollenzuordnungen von <strong style={stil('color:var(--text-primary)')}>„{quelle.name}“</strong>
          {quelle.kategorie ? ` (${quelle.kategorie})` : ''}{' '}
          gehen auf ein anderes Asset derselben Objektart über. Was dort schon zugeordnet ist,
          wird übersprungen.
          {ungespeichert && ' Ungespeicherte Eingaben im Formular gehen dabei nicht mit.'}
        </p>

        <div style={stil('margin-bottom:20px')}>
          {abschnitt('Ziel')}
          <div role="radiogroup" aria-label="Ziel des Ersetzens" style={stil(
            'display:flex;gap:16px;flex-wrap:wrap;margin-bottom:12px',
          )}>
            <label style={stil('display:flex;align-items:center;gap:8px;min-height:44px;font-size:13px;cursor:pointer')}>
              <input type="radio" name="ersetzen-art" checked={art === 'vorhanden'}
                     onChange={() => artWaehlen('vorhanden')}
                     style={stil('width:16px;height:16px;accent-color:var(--brand-primary)')} />
              Vorhandenes Asset
            </label>
            <label style={stil('display:flex;align-items:center;gap:8px;min-height:44px;font-size:13px;cursor:pointer')}>
              <input type="radio" name="ersetzen-art" checked={art === 'neu'}
                     onChange={() => artWaehlen('neu')}
                     style={stil('width:16px;height:16px;accent-color:var(--brand-primary)')} />
              Neues Asset anlegen
            </label>
          </div>

          {art === 'vorhanden' ? (
            <>
              <input value={suche} onChange={(e) => setzeSuche(e.target.value)}
                     className="formularfeld" type="search" autoComplete="off" spellCheck={false}
                     aria-label="Ziel suchen" placeholder="Name des Ziels suchen…"
                     style={stil('margin-bottom:8px')} />
              <div role="radiogroup" aria-label="Vorhandene Assets" style={stil(
                'border:1px solid var(--border-subtle);border-radius:var(--r-md);overflow:hidden;'
                + 'max-height:260px;overflow-y:auto',
              )}>
                {kandidaten.laedt && liste.length === 0 && (
                  <p style={stil('margin:0;padding:12px 14px;font-size:13px;color:var(--text-secondary)')}>
                    Wird gesucht…
                  </p>
                )}
                {!kandidaten.laedt && liste.length === 0 && (
                  <p style={stil('margin:0;padding:12px 14px;font-size:13px;color:var(--text-secondary)')}>
                    {gesucht ? `Kein Asset dieser Art passt zu „${gesucht}“.` : 'Kein weiteres Asset dieser Art.'}
                  </p>
                )}
                {liste.map((a, i) => (
                  <label key={a.assetId} className="rz-zeile" style={stil(
                    'display:flex;align-items:center;gap:10px;min-height:44px;padding:6px 14px;cursor:pointer;'
                    + (i > 0 ? 'border-top:1px solid var(--border-subtle)' : ''),
                  )}>
                    <input type="radio" name="ersetzen-ziel" checked={ziel?.assetId === a.assetId}
                           onChange={() => zielWaehlen(a)}
                           style={stil('flex:0 0 auto;width:16px;height:16px;accent-color:var(--brand-primary)')} />
                    <span style={stil('flex:1;min-width:0;font-size:13px;overflow-wrap:anywhere')}>{a.name}</span>
                    {/* Die Kategorie immer — sie unterscheidet Zwillinge gleichen
                        Namens (AVD/Surface). */}
                    <span style={stil('flex:0 1 auto;font-size:12px;color:var(--text-tertiary)')}>
                      {a.kategorie || 'ohne Kategorie'}
                    </span>
                  </label>
                ))}
              </div>
              {(kandidaten.daten?.gesamt ?? 0) > 20 && (
                <p style={stil('margin:6px 0 0;font-size:12px;color:var(--text-tertiary)')}>
                  Gezeigt werden die ersten 20 Treffer — die Suche grenzt weiter ein.
                </p>
              )}
            </>
          ) : (
            <>
              <label htmlFor="ersetzen-neu" style={stil(
                'display:block;font-size:13px;line-height:18px;font-weight:500;margin-bottom:4px',
              )}>Name des neuen Assets</label>
              <input id="ersetzen-neu" value={neuName}
                     onChange={(e) => { setzeNeuName(e.target.value); zurueck(); }}
                     className="formularfeld" autoComplete="off" spellCheck={false}
                     aria-invalid={!neuMoeglich || !neuerName || neuGleichQuelle}
                     aria-describedby="ersetzen-neu-hinweis" />
              <div id="ersetzen-neu-hinweis" style={stil('margin-top:4px;font-size:12px;color:var(--text-tertiary)')}>
                {!neuMoeglich
                  ? 'Aus diesem Formular entstünde eine andere Objektart. Bitte das neue Asset zuerst anlegen und hier als vorhandenes wählen.'
                  : neuGleichQuelle
                    ? 'Name und Kategorie sind dieselben wie beim alten Asset — eines davon muss sich unterscheiden.'
                    : `Kategorie: ${neueKategorie || 'ohne'}. `
                      + 'Alle übrigen Angaben wie bei „Als Kopie anlegen“. Das Asset entsteht mit '
                      + 'der Vorschau und bleibt bestehen, auch wenn Sie danach abbrechen.'}
              </div>
            </>
          )}
        </div>

        <div style={stil('margin-bottom:20px')}>
          {abschnitt('Ausführung')}
          <label style={stil('display:flex;align-items:center;gap:8px;min-height:44px;font-size:13px;cursor:pointer')}>
            <input type="checkbox" checked={archivieren}
                   onChange={(e) => { setzeArchivieren(e.target.checked); zurueck(); }}
                   style={stil('width:16px;height:16px;accent-color:var(--brand-primary)')} />
            {ARCHIV_LABEL[quelle.assetTyp] ?? 'Altes Asset archivieren'}
          </label>
          <label htmlFor="ersetzen-grund" style={stil(
            'display:block;font-size:13px;line-height:18px;font-weight:500;margin:8px 0 4px',
          )}>
            Begründung<span style={stil('color:var(--danger)')}> *</span>
          </label>
          <input id="ersetzen-grund" value={grund} onChange={(e) => setzeGrund(e.target.value)}
                 className="formularfeld" aria-invalid={grund !== '' && !grundOk}
                 aria-describedby="ersetzen-grund-hinweis"
                 placeholder="z. B. Ablösung durch die Nachfolgeversion" />
          <div id="ersetzen-grund-hinweis" style={stil('margin-top:4px;font-size:12px;color:var(--text-tertiary)')}>
            Mindestens {GRUND_MINDESTENS} Zeichen. Sie steht im Protokoll und — wo eine Freigabe
            nötig ist — bei der Person, die zustimmt.
          </div>
        </div>

        {/* Die Zahlen werden angesagt, sobald sie da sind. */}
        <div role="status" aria-live="polite">
          {vorschau && (
            <div style={stil(
              'padding:12px 14px;background:var(--surface-sunken);border-radius:var(--r-md);'
              + 'font-size:13px;line-height:20px',
            )}>
              <strong style={stil('font-weight:600')}>
                {zahl(vorschau.uebernommen)} {vorschau.uebernommen === 1 ? 'Zuordnung wird' : 'Zuordnungen werden'} übertragen
              </strong>
              {vorschau.uebersprungen > 0 && (
                <> · {zahl(vorschau.uebersprungen)} {vorschau.uebersprungen === 1 ? 'ist' : 'sind'} am Ziel schon vorhanden</>
              )}
              {archivieren && <> · „{quelle.name}“ wird archiviert</>}
              .
              <div style={stil('margin-top:4px;color:var(--text-secondary)')}>
                Wo eine Freigabe nötig ist, wird jede Zeile einzeln entschieden. Bis dahin können
                altes und neues Asset beide teilweise zugeordnet sein.
              </div>
            </div>
          )}
        </div>
        {fehler && (
          <p role="alert" style={stil('margin:12px 0 0;font-size:13px;color:var(--danger)')}>{fehler}</p>
        )}
      </div>

      <div style={stil(
        'flex:0 0 auto;display:flex;align-items:center;flex-wrap:wrap;gap:10px;padding:12px 20px;'
        + 'border-top:1px solid var(--border-subtle);background:var(--surface-card)',
      )}>
        <button type="button" className="knopf knopf--rand" onClick={onZurueck} disabled={laeuft}>
          Zurück zum Formular
        </button>
        <span style={stil('flex:1')} />
        <button type="button" className="knopf knopf--rand"
                disabled={!nach || !grundOk || laeuft}
                title={!nach ? 'Erst ein Ziel wählen.' : !grundOk ? 'Erst eine Begründung angeben.' : undefined}
                onClick={() => { void vorschauRechnen(); }}>
          {laeuft && !vorschau ? 'Wird gezählt…'
            : vorschau ? 'Vorschau neu berechnen'
              : art === 'neu' ? 'Asset anlegen und Vorschau berechnen' : 'Vorschau berechnen'}
        </button>
        <button type="button" className="knopf knopf--haupt"
                disabled={!nach || !grundOk || !vorschau || laeuft}
                title={!vorschau ? 'Erst die Vorschau berechnen.' : undefined}
                onClick={() => { void ausfuehren(); }}>
          {laeuft && vorschau ? 'Wird ersetzt…' : 'Ersetzen ausführen'}
        </button>
      </div>
    </>
  );
}
